/**
 * Onboarding endpoints (backend `modules/onboarding`, 2026-10-08).
 * The rules live in the backend (`onboarding.rules.ts`); these screens only show what
 * the API returns and send the contractor's answers.
 */
import { http, httpUpload, type HttpResult } from './client';

export type OnboardingStage = 'offer_question' | 'profile_only' | 'onboarding' | 'complete';

export interface OnboardingTask {
  key: string;
  label: string;
  done: boolean;
  /** When the step was done, when the backend knows it. */
  doneAt?: string;
}

export interface OnboardingStatus {
  stage: OnboardingStage;
  offerAnswer: 'yes' | 'no' | null;
  onboardingOpen: boolean;
  teamAdded: boolean;
  workerType: '1099' | 'w2' | 'V.C.' | null;
  workerTypeAnswers: { isCalifornia: boolean; hasEin: boolean; hasCoi: boolean } | null;
  /** Third party company or outsourced tech: the team onboards them, no contractor steps. */
  thirdParty?: boolean;
  /** Ask "Are you a company providing labor?" before the worker type questions. */
  askCompanyQuestion?: boolean;
  laborCompanyAnswer?: 'yes' | 'no' | null;
  profileTasks: OnboardingTask[];
  profileComplete: boolean;
  tasks: OnboardingTask[];
  complete: boolean;
  hrApproved: boolean;
  verified: boolean;
  verifiedOn: string | null;
  verifiedBy: string | null;
  verifyNote: string | null;
  payrollApproved: boolean;
  workMarketId?: string | null;
  adpInviteRequestedAt: string | null;
  adpOnboardingFinishedOn: string | null;
  onboardingSentAt: string | null;
  onboardingSentBy: string | null;
}

export type QueueView = 'verify' | 'in_progress' | 'waiting' | 'booked';

/** The soonest show of someone in the "Booked, needs HR" list. */
export interface QueueNextShow {
  eventId: string;
  eventName: string;
  jobNumber: number | null;
  /** First day they work on it (YYYY-MM-DD, show calendar day). */
  day: string;
  /** 0 = today. */
  daysUntil: number;
}

export interface QueueRow {
  _id: string;
  firstName: string;
  lastName: string;
  preferredName: string | null;
  email: string | null;
  mobile: string | null;
  profileImage: string | null;
  city: string | null;
  state: string | null;
  contractorType: string | null;
  createdAt: string | null;
  stage: OnboardingStage;
  tasksDone: number;
  tasksTotal: number;
  missing: string[];
  /** Every onboarding step, done or not. */
  steps: OnboardingTask[];
  /** Profile steps (address/birthdate/emergency contact, roles), for people with no onboarding yet. */
  profileSteps: OnboardingTask[];
  hrApproved: boolean;
  verified: boolean;
  offerAnswer: 'yes' | 'no' | null;
  onboardingSentAt: string | null;
  onboardingSentBy: string | null;
  // Read by the row's ⋮ menu (the same menu as the Contractors list).
  isOnBoarding: boolean;
  isContractorFlagged: boolean;
  isContractorRestricted: boolean;
  smsPermissionStatus: boolean;
  smsConsentStatusBySms: boolean;
  /** Only in the "booked" view. */
  nextShow?: QueueNextShow;
  /** Only in the "booked" view: upcoming or ongoing shows they are on. */
  showCount?: number;
}

type ApiErrorShape = { response?: { data?: { message?: unknown } } };

/** The backend sends errors as `{ message }`; this keeps one readable string. */
export const apiErrorMessage = (error: unknown, fallback = 'Something went wrong. Please try again.') => {
  const m = (error as ApiErrorShape | null)?.response?.data?.message;
  if (Array.isArray(m)) return m.join(' ');
  return typeof m === 'string' && m.trim() ? m : fallback;
};

const data = <T>(result: HttpResult): T => {
  if (result.error) throw result.error;
  const body = result.response?.data as { data?: T } | T | undefined;
  return ((body as { data?: T } | undefined)?.data ?? body) as T;
};

export const fetchOnboardingStatus = async (contractorId?: string) =>
  data<OnboardingStatus>(
    await http({
      method: 'GET',
      url: 'onboarding/status',
      params: contractorId ? { contractorId } : undefined,
      isToken: true,
    }),
  );

export const answerOfferQuestion = async (offered: boolean) =>
  data<OnboardingStatus>(
    await http({ method: 'POST', url: 'onboarding/offer-answer', data: { offered }, isToken: true }),
  );

/** "Are you a company providing labor (contractors) for ShowPhaze? (Third Party Work)" */
export const answerLaborCompany = async (isCompany: boolean) =>
  data<OnboardingStatus>(
    await http({ method: 'POST', url: 'onboarding/labor-company', data: { isCompany }, isToken: true }),
  );

export const answerWorkerType = async (body: {
  isCalifornia: boolean;
  hasEin: boolean;
  hasCoi: boolean;
  einNumber?: string;
}) =>
  data<OnboardingStatus>(
    await http({ method: 'POST', url: 'onboarding/worker-type', data: body, isToken: true }),
  );

export const saveWorkMarketId = async (workBookId: string) =>
  data<OnboardingStatus>(
    await http({ method: 'PUT', url: 'onboarding/workmarket-id', data: { workBookId }, isToken: true }),
  );

export const saveEin = async (einNumber: string) =>
  data<OnboardingStatus>(
    await http({ method: 'PUT', url: 'onboarding/ein', data: { einNumber }, isToken: true }),
  );

export const requestAdpInvite = async () =>
  data<OnboardingStatus & { notified: boolean }>(
    await http({ method: 'POST', url: 'onboarding/adp-invite-request', isToken: true }),
  );

export const saveAdpFinishedDate = async (date: string) =>
  data<OnboardingStatus>(
    await http({ method: 'PUT', url: 'onboarding/adp-finished', data: { date }, isToken: true }),
  );

/** SMS consent, same endpoint the consent screen uses. */
export const acceptSmsConsent = async (userId: string) =>
  data<unknown>(
    await http({
      method: 'PUT',
      url: 'updateContractorSmsStatus',
      params: { userId },
      data: { smsPermissionStatus: true, termsAcceptedAt: true },
      isToken: true,
    }),
  );

/** COI upload, same multipart endpoint as the profile wizard. */
export const uploadCoi = async (file: File) => {
  const form = new FormData();
  form.append('insuranceCertificate', file);
  form.append('self', 'true');
  return data<unknown>(await httpUpload('uploadProfileImageAndResume', form, 'PATCH'));
};

// ── public sign up ──────────────────────────────────────────────────────

export interface ContractorSignupInput {
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  termsAccepted: boolean;
  smsAccepted: boolean;
}

/**
 * Public contractor sign up. The account only needs a name, an email and a phone
 * number; the profile is filled in after the first login (onboarding rules, 2026-10-07).
 * The backend emails the password.
 */
export const signUpContractor = async (input: ContractorSignupInput) =>
  data<{ email: string }>(
    await http({
      method: 'POST',
      url: 'selfSignup',
      data: {
        firstName: input.firstName.trim(),
        lastName: input.lastName.trim(),
        email: input.email.trim().toLowerCase(),
        mobile: input.mobile.replace(/\D/g, ''),
        termsAcceptedAt: input.termsAccepted,
        smsPermissionStatus: input.smsAccepted,
      },
    }),
  );

// ── team ────────────────────────────────────────────────────────────────

export const sendOnboarding = async (contractorId: string) =>
  data<{
    message: string;
    emailSent: boolean;
    textSent: boolean;
    textSkippedReason: string | null;
    status: OnboardingStatus;
  }>(await http({ method: 'POST', url: `onboarding/send/${contractorId}`, isToken: true }));

export const setVerified = async (contractorId: string, verified: boolean, note?: string) =>
  data<{ status: OnboardingStatus }>(
    await http({
      method: 'PATCH',
      url: `onboarding/verify/${contractorId}`,
      data: { verified, note: note ?? '' },
      isToken: true,
    }),
  );

export const fetchOnboardingQueue = async (view: QueueView, page = 1, limit = 25, search = '') => {
  const result = await http({
    method: 'GET',
    url: 'onboarding/queue',
    params: { view, page, limit, ...(search.trim() ? { search: search.trim() } : {}) },
    isToken: true,
  });
  if (result.error) throw result.error;
  return result.response?.data as { total: number; page: number; limit: number; data: QueueRow[] };
};

export const fetchOnboardingQueueCounts = async () => {
  const result = await http({ method: 'GET', url: 'onboarding/queue-counts', isToken: true });
  if (result.error) throw result.error;
  return result.response?.data as { verify: number; waiting: number; booked?: number };
};
